// The backdrop behind one popover: which kind it gets, the element, and its fade. Kept apart
// from PopoverHostComponent so the default for an unset option lives in one place
// (df-913afaedfad3: it used to be written out three times).

export enum BackdropMode {
  None = 'none',           // No backdrop at all
  Invisible = 'invisible', // Invisible backdrop (for detecting outside clicks)
  Visible = 'visible'      // Visible backdrop with blur/dimming
}

export interface BackdropOptions {
  backdropMode?: BackdropMode; // Replaces showBackdrop
  showBackdrop?: boolean; // Deprecated, kept for compatibility
}

// an explicit backdropMode wins; otherwise the deprecated showBackdrop flag decides, and with
// neither set the popover gets an invisible backdrop that still catches outside clicks
export function resolveBackdropMode(opts: BackdropOptions): BackdropMode {
  if (opts.backdropMode) {
    return opts.backdropMode;
  }
  if (opts.showBackdrop === false) {
    return BackdropMode.None;
  }
  if (opts.showBackdrop === true) {
    return BackdropMode.Visible;
  }
  return BackdropMode.Invisible;
}

// adds the backdrop to the host, just below its popover; onClick is left out when a click on
// the backdrop must not dismiss
export function createBackdropElement(host: HTMLElement, mode: BackdropMode, zIndex: number, onClick?: () => void): HTMLElement {
  const backdrop = document.createElement('div');
  backdrop.className = 'popover-backdrop';
  backdrop.style.zIndex = String(zIndex);
  backdrop.classList.add(mode === BackdropMode.Visible ? 'backdrop-visible' : 'backdrop-invisible');

  if (onClick) {
    backdrop.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      onClick();
    });
  }

  host.appendChild(backdrop);
  return backdrop;
}

// takes the backdrop out of the page; answers false when it was not in the page to begin with
export function removeBackdropElement(backdrop: HTMLElement): boolean {
  if (!backdrop.parentElement) {
    return false;
  }
  backdrop.parentElement.removeChild(backdrop);
  return true;
}

export enum BackdropFade {
  In = 'in',
  Out = 'out',
}

const backdropClear = { background: 'transparent', backdropFilter: 'blur(0px)' };
const backdropDimmed = { background: 'var(--shadow-color)', backdropFilter: 'blur(1px)' };

// fades a backdrop in or out over the given duration and answers the running animation
export function fadeBackdrop(backdrop: HTMLElement, fade: BackdropFade, durationMs: number): Animation {
  const keyframes = fade === BackdropFade.In ? [backdropClear, backdropDimmed] : [backdropDimmed, backdropClear];
  return backdrop.animate(keyframes, {
    duration: durationMs,
    fill: 'forwards',
    easing: 'ease-out',
  });
}
